import React, {forwardRef, HTMLAttributes, MutableRefObject, memo, Ref} from 'react';
import classNames from 'classnames';

import { CloseButton, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react'
import { useState } from 'react'

import Action from '../Action/Action.tsx';
import Handle from '../Handle/Handle.tsx';
import Remove from '../Remove/Remove.tsx';
import Duplicate from '../Duplicate/Duplicate.tsx';
import IsolateItem from '../IsolateItem/IsolateItem.tsx';
import styles from './TreeItem.module.css';
import TaskTextField from '../../../TaskTextField.js';

import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { ContentCopy, Delete, EmojiEvents, ExpandMore, ExpandLess, MoreHoriz, EditCalendar } from '@mui/icons-material';
import { Box, ListItemIcon } from '@mui/material';

export interface Props extends Omit<HTMLAttributes<HTMLLIElement>, 'id'> {
  childCount?: number;
  clone?: boolean;
  collapsed?: boolean;
  estimatedCommencementDate?: string;
  idealCompletionDate?: string;
  latestPossibleCompletionDate?: string;
  done?: boolean;
  depth: number;
  disableInteraction?: boolean;
  disableSelection?: boolean;
  draggable?: boolean;
  ghost?: boolean;
  goal?: boolean;
  handleProps?: any;
  indicator?: boolean;
  indentationWidth: number;
  isMobile?: boolean;
  isInitiallyExpanded?: boolean;
  primaryLabel: string;
  selected?: boolean;
  status: string;
  value: string;  
  onCollapse?(): void;
  onDoneChanged?(): void;
  onExpansionChanged?(isExpanded: boolean): void;
  onGoalChanged?(): void;
  onEstimatedCommencementDateChanged?(newDate: string): void;
  onIdealDateChanged?(newDate: string): void;
  onPrimaryLabelChanged?(newPrimaryLabel: string): void;
  onLatestDateChanged?(newDate: string): void;
  onStatusChanged?(newStatus: string): void;
  onTitleChanged?(newTitle: string): void;
  onSelected?(): void;
  treeItemHandleOnfocus?(ctrlCreate: boolean ): void;
  handleUpArrowPressed?(): void;
  handleDownArrowPressed?(): void;
  handleOnSubmit?(): void;
  onDuplicate?(): void;
  onRemove?(): void;
  onNavTo?(): void;
  wrapperRef?(node: HTMLLIElement): void;
  inputRef?: MutableRefObject<null> | null;
  inputRefs: MutableRefObject<never[]> | null;
  index: number;
}

const states = [
  { id: 1, name: 'New' },
  { id: 2, name: 'Processing' },
  { id: 3, name: 'Ready' },
  { id: 4, name: 'Scheduled' },  
  { id: 5, name: 'In Progress' },  
  { id: 6, name: 'Completed' },
]

const primaryLabels = [
  { id: 1, name: 'None' },
  { id: 2, name: 'Career' },
  { id: 3, name: 'Finances' },
  { id: 4, name: 'Health' },
  { id: 5, name: 'Interests' },
  { id: 6, name: 'Knowledge' },  
  { id: 7, name: 'Lifestyle' },
  { id: 8, name: 'Organisation' },
  { id: 9, name: 'Social' }, 
  { id: 10, name: 'Relationships' },
]

export const TreeItem = React.memo(forwardRef<HTMLDivElement, Props>(
  (
    {
      childCount,
      clone,
      collapsed,
      estimatedCommencementDate = '',
      idealCompletionDate = '',
      latestPossibleCompletionDate = '',
      done,
      depth,
      disableSelection,
      disableInteraction,
      draggable = true,
      ghost,
      goal,
      handleProps,
      indentationWidth,
      indicator,
      isMobile = false,
      isInitiallyExpanded = false,
      primaryLabel,
      // selected = false,
      status,      
      onCollapse,
      onDoneChanged,
      onExpansionChanged,
      onGoalChanged,
      onEstimatedCommencementDateChanged,
      onIdealDateChanged,
      onLatestDateChanged,      
      onPrimaryLabelChanged,
      onStatusChanged,
      onTitleChanged,
      onSelected,
      treeItemHandleOnfocus,
      handleUpArrowPressed,
      handleDownArrowPressed,
      handleOnSubmit,
      onDuplicate,
      onRemove,
      onNavTo,
      style,
      value,
      wrapperRef,
      inputRef,
      inputRefs,
      index,
      ...props
    },
    ref
  ) => {

    const initialState = states.find(({name}) => name === status);
    
    let initialPrimaryLabel;
    let passedPrimaryLabel;
    if(primaryLabel) {
      passedPrimaryLabel = primaryLabels.find(({name}) => name === primaryLabel);
      if(passedPrimaryLabel){
        initialPrimaryLabel = passedPrimaryLabel;
      }
      else {
        initialPrimaryLabel = {id:1, name:primaryLabel}; //TODO: Not sure what this is.
      }
    }
    else {
      initialPrimaryLabel = primaryLabels[0];
    }

    const [selectedState, setSelectedState] = useState(initialState ? initialState: states[0]);
    const [selectedPrimaryLabel, setSelectedPrimaryLabel] = useState(initialPrimaryLabel ? initialPrimaryLabel: primaryLabels[0]);
    
    const [highlighted, setHighlighted] = useState(false);
    const [selected, setSelected] = useState(false);
    const [selectable, setSelectable] = useState(true);
    const [isExpanded, setIsExpanded] = useState(isInitiallyExpanded);

    const [query, setQuery] = useState('');

    // if(onStatusChanged && initialState){
    //   onStatusChanged(initialState.name);
    // }

    function handleStatusChange(state) {
      setSelectedState(state);
      onStatusChanged(state.name);
    }

    function handlePrimaryLabelChange(primaryLabel) {
      setSelectedPrimaryLabel(primaryLabel);
      if(primaryLabel) {
        onPrimaryLabelChanged(primaryLabel.name);
      }
    }

    function handleClick(e) {
      //TODO: Set state to change appearance - a golden ring around the whole item for example.
      e.preventDefault();
      e.stopPropagation();
      console.log('MousePointn\'? '+e.pointerType);
      console.log('Clickn\'? '+selectable+ ' '+ onSelected);
      if(e.pointerType || e.nativeEvent?.pointerType === 'mouse') {
        console.log('Selectn\'? '+selectable);
        selectable && 
        onSelected && onSelected();
      }
    }

    function handleMouseEntersHighlightArea(e) { 
      // e.preventDefault();
      // e.stopPropagation();
      //TODO: Set state to change appearance - a golden ring around the whole item for example.
      selectable && setHighlighted(true);
    }

    function handleMouseLeavesHighlightArea() {
      //TODO: Set state to change appearance - a golden ring around the whole item for example.
      setHighlighted(false);
    }

    function handleMouseEntersNonSelectableHighlightArea() {
      //TODO: Set state to change appearance - a golden ring around the whole item for example.
      selectable && setHighlighted(true);
      setSelectable(false);
    }

    function handleMouseLeavesNonSelectableHighlightArea() {
      //TODO: Set state to change appearance - a golden ring around the whole item for example.
      setHighlighted(false);
      setSelectable(true);
    }

    function handleMouseEntersItemNonSelectionArea(e) {
      //TODO: Set state to change appearance - a golden ring around the whole item for example.
      setSelectable(false);
    }

    function handleMouseLeavesItemNonSelectionArea() {
      //TODO: Set state to change appearance - a golden ring around the whole item for example.
      setSelectable(true);
    }

    const id = React.useId();
    const buttonId = `${id}-button`;
    const menuId = `${id}-menu`;
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleContextMenuClick = (event) => {
      event.preventDefault();
      setAnchorEl(event.currentTarget);
    };

    const handleClose = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setAnchorEl(null);
    };

    const handleDuplicateMenuItemClicked = (e) => {
      handleClose(e);
      
      setTimeout(() => {
        onDuplicate && onDuplicate();
      }, 0); 
      
    }

    const handleRemoveMenuItemClicked = (e) => {
      handleClose(e);      

      onRemove();
    }

    const handleRescheduleTomorrowSameTimeMenuItemClicked = (e) => {
      handleClose(e);

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0); // Sets time to 09:00:00.000
      if(onIdealDateChanged) {

        const startTimeMs = tomorrow.getTime();
        const utcDate = new Date(startTimeMs);
        const localTime = startTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
        const localDate = new Date(localTime);
        const startTimeString = localDate.toISOString().replace("Z", "");

        tomorrow.setHours(9,30,0)
        
        const endTimeMs = tomorrow.getTime();
        const utcDate2 = new Date(endTimeMs);
        const localTime2 = endTimeMs - utcDate2.getTimezoneOffset() * 60 * 1000;
        const localDate2 = new Date(localTime2);
        const endTimeString = localDate2.toISOString().replace("Z", "");

        setTimeout(() => {
            onIdealDateChanged(startTimeString);
            onLatestDateChanged && onLatestDateChanged(endTimeString);
          }, 0); 
      }
    }

    const handleRescheduleNextWeekSameTimeMenuItemClicked = (e) => {
      handleClose(e);

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 7);
      tomorrow.setHours(9, 0, 0); // Sets time to 09:00:00.000
      if(onIdealDateChanged) {

        const startTimeMs = tomorrow.getTime();
        const utcDate = new Date(startTimeMs);
        const localTime = startTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
        const localDate = new Date(localTime);
        const startTimeString = localDate.toISOString().replace("Z", "");

        tomorrow.setHours(9,30,0)
        
        const endTimeMs = tomorrow.getTime();
        const utcDate2 = new Date(endTimeMs);
        const localTime2 = endTimeMs - utcDate2.getTimezoneOffset() * 60 * 1000;
        const localDate2 = new Date(localTime2);
        const endTimeString = localDate2.toISOString().replace("Z", "");

        onIdealDateChanged(startTimeString);
        onLatestDateChanged && onLatestDateChanged(endTimeString);
      }
    }

    function handleOnExpandClicked() {      
      setIsExpanded(true);
      onExpansionChanged && onExpansionChanged(true);
    }

    function handleOnCloseExpandClicked() {
      setIsExpanded(false);
      onExpansionChanged && onExpansionChanged(false);
    }

    const handleMenuOpen = (e) => {
      // 💡 Isolate the tap completely to prevent focus propagation loops
      e.preventDefault();
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
    };

    const filteredPrimaryLabels =
    query === ''
      ? primaryLabels
      : primaryLabels.filter((primaryLabel) => {
          return primaryLabel.name.toLowerCase().includes(query.toLowerCase())
        })

    //TODO: Fix the possible invocation of null event handlers. Default handler(s)?

    // There are esoteric differences between onInput and onChange - even though it seems 
    // one should be able use either (interchangably?) for the same purpose(s)...
    return (
      <li 

        onClick={ (e) => handleClick(e) }
  //       onPointerDown={ (e) => { e.preventDefault();
  //                                e.stopPropagation();
  //                               //  setIsPressed(true);
  //                               }
  //                     }
  //       onPointerUp={ (e) => { e.preventDefault();
  //                              e.stopPropagation();
  //                               //  setIsPressed(true);
  //                            }
  //                   }
  //                     onMouseDown={(e) => {
  //   e.preventDefault();
  //                              e.stopPropagation();
  //   // setIsPressed(true);
  // }}
  // onMouseUp={(e) => {
  //   e.preventDefault();
  //                              e.stopPropagation();
  //   // setIsPressed(false);
  // }}
  //   onTouchStart={(e) => {
  //   e.stopPropagation();
  //   // setIsPressed(true);
  // }}
  // onTouchEnd={(e) => {
  //   // e.preventDefault();
  //   e.stopPropagation();
  //   // setIsPressed(false);
  // }}
        className={classNames(
          styles.Wrapper,
          clone && styles.clone,
          ghost && styles.ghost,
          indicator && styles.indicator,
          disableSelection && styles.disableSelection,
          disableInteraction && styles.disableInteraction,
          done && styles.TreeItemDone
        )}
        ref={wrapperRef}
        style={
          {
            '--spacing': `${indentationWidth * depth}px`,
          } as React.CSSProperties
        }
        {...props}
      >
      <div className={classNames(styles.SelectableTreeItem)}
             onMouseEnter={handleMouseEntersHighlightArea}
             onMouseLeave={handleMouseLeavesHighlightArea}
             onClick={(e) => handleMouseEntersHighlightArea(e)}
             touchaction>
        <div className={classNames(styles.LeftButtons)}>
          <Action
            onClick={onCollapse}
            // onMouseEnter={handleMouseEntersNonSelectableHighlightArea}
            // onMouseLeave={handleMouseLeavesNonSelectableHighlightArea}
            onMouseEnter={handleMouseEntersItemNonSelectionArea} 
            onMouseLeave={handleMouseLeavesItemNonSelectionArea}
            className={classNames(
              styles.Collapse,
              !onCollapse && styles.Hidden,
              collapsed && styles.collapsed
            )}
          >
            {collapseIcon}
          </Action>
        </div>
        <div className={classNames(styles.TreeItem, highlighted && styles.TreeItemHighlighted)}
             ref={ref}
             style={style}>
          <div className={styles.Centre}>

            {draggable && (
              <Handle className={classNames(
                styles.Handle,
                !highlighted && styles.NotDisplayed
              )} {...handleProps} />
            )}

            <Action
              onClick={onGoalChanged}
              onMouseEnter={handleMouseEntersItemNonSelectionArea} 
              onMouseLeave={handleMouseLeavesItemNonSelectionArea}
              className={classNames(
                styles.NotGoal,
                goal && styles.goal
              )}
            >
              <EmojiEvents />
            </Action>

            <div className={styles.Vars}>
              <span className={styles.Text}>
                {/* <Box
                  sx={(theme) => ({ 
                    display: 'none',
                    [theme.breakpoints.up('xs')]: {
                      display: 'block', // Snaps cleanly via pure CSS layout
                    }
                  })}>
                <TaskTextField  title={value} 
                                onTitleChange={onTitleChanged}
                                onUpArrowPressed={handleUpArrowPressed} 
                                onDownArrowPressed={handleDownArrowPressed} 
                                handleOnFocus={treeItemHandleOnfocus} 
                                handleOnSubmit={handleOnSubmit} ref={inputRef} 
                                onMouseEnter={handleMouseEntersItemNonSelectionArea} 
                                onMouseLeave={handleMouseLeavesItemNonSelectionArea} 
                                // textFieldCharacterLength={55} 
                                inputRefs={inputRefs} 
                                index={index} />
              </Box>
              <Box
                  sx={(theme) => ({ 
                    display: 'none',
                    [theme.breakpoints.down('xs')]: {
                      display: 'block', // Snaps cleanly via pure CSS layout
                    }
                  })}> */}
                <TaskTextField  title={value} 
                                onTitleChange={onTitleChanged}
                                onUpArrowPressed={handleUpArrowPressed} 
                                onDownArrowPressed={handleDownArrowPressed} 
                                handleOnFocus={treeItemHandleOnfocus} 
                                handleOnSubmit={handleOnSubmit} ref={inputRef} 
                                onMouseEnter={handleMouseEntersItemNonSelectionArea} 
                                onMouseLeave={handleMouseLeavesItemNonSelectionArea} 
                                textFieldCharacterLength={isMobile? ((highlighted || isExpanded) ? 17: 31) : 70}
                                inputRefs={inputRefs} 
                                index={index} />
              </span>

              {(highlighted || isExpanded) &&
              <div className={styles.Vars}>

                <label className={styles.Checkbox}>
                  <input name='doneCheckbox' className={styles.Checkbox} type='checkbox' defaultChecked={done} onInput={onDoneChanged}/>
                  {/* Done? */}
                </label>

                <Action
                  onClick={isExpanded ? handleOnCloseExpandClicked : handleOnExpandClicked }
                  onMouseEnter={handleMouseEntersItemNonSelectionArea} 
                  onMouseLeave={handleMouseLeavesItemNonSelectionArea}
                  className={classNames(
                    styles.NotExpanded,
                    isExpanded && styles.Expanded
                  )} >

                  {!isExpanded &&
                  <ExpandMore />}

                  {isExpanded &&
                  <ExpandLess />}

                </Action>
              </div>}

            </div>
          </div>

          {isExpanded &&
          <div className={styles.TaskExpandedDetails}>
            <div className={styles.Dates}>
              <label  className={styles.Dates}>
                Estimated Commencement:
                <div className={styles.DateInput}>
                  <input name='estimatedCommencementDateTime' className={styles.DateInput} type='datetime-local'
                         onClick={(e) => e.stopPropagation()}
                         onMouseDown={(e) => e.stopPropagation()}
                         value={estimatedCommencementDate} 
                         onMouseEnter={handleMouseEntersItemNonSelectionArea} 
                         onMouseLeave={handleMouseLeavesItemNonSelectionArea}
                         onChange={(e) => onEstimatedCommencementDateChanged(e.target.value)}/>
                </div>
              </label>
              <label className={styles.Dates}>
                Scheduled Start/Ideal Completion
                <div className={styles.DateInput}>
                  <input name='idealCompletionDateTime' className={styles.DateInput} type='datetime-local'
                         onClick={(e) => e.stopPropagation()}
                         onMouseDown={(e) => e.stopPropagation()}
                         value={idealCompletionDate} 
                         onMouseEnter={handleMouseEntersItemNonSelectionArea} 
                         onMouseLeave={handleMouseLeavesItemNonSelectionArea}
                         onChange={(e) => onIdealDateChanged(e.target.value)}/>
                </div>
              </label>
              <label className={styles.Dates}>
                Scheduled End/Latest Possible
                <div className={styles.DateInput}>
                  <input name='latestPossibleCompletionDateTime' className={styles.DateInput} type='datetime-local'
                         onClick={(e) => e.stopPropagation()}
                         onMouseDown={(e) => e.stopPropagation()}
                         value={latestPossibleCompletionDate} 
                         onMouseEnter={handleMouseEntersItemNonSelectionArea} 
                         onMouseLeave={handleMouseLeavesItemNonSelectionArea}
                         onChange={(e) => onLatestDateChanged(e.target.value)}/>
                </div>
              </label>
              {/* <Action
                onClick={handleOnCloseExpandClicked}
                onMouseEnter={handleMouseEntersItemNonSelectionArea} 
                onMouseLeave={handleMouseLeavesItemNonSelectionArea}
                className={classNames(
                  styles.NotExpanded,
                  isExpanded && styles.Expanded
                )}
                >
                  <ExpandLess />
              </Action> */}
            </div>
          </div>}
        </div>

        {clone && childCount && childCount > 1 ? (
          <span className={styles.Count}>{childCount}</span>
        ) : null}
        
        {(!isMobile || highlighted) && (
        <div className={classNames(styles.RightButtons, !highlighted && styles.Hidden)} onMouseEnter={handleMouseEntersItemNonSelectionArea} 
            onMouseLeave={handleMouseLeavesItemNonSelectionArea}>
          {!clone && onNavTo && <IsolateItem className={styles.RightButton} onClick={onNavTo}/>}
          <Button
            id={buttonId}
            aria-controls={open ? menuId : undefined}
            aria-haspopup="true"
            aria-expanded={open}
            onMouseDown={handleMenuOpen}
            onClick={(e) => handleContextMenuClick(e)}
            onTouchEnd={handleContextMenuClick}
          >
            <MoreHoriz />
          </Button>
          {/* {!clone && onDuplicate && <Duplicate className={styles.RightButton} onClick={onDuplicate}/>}
          {!clone && onRemove && <Remove className={styles.RightButton} onClick={onRemove}/>} */}
          <Listbox value={selectedState} onChange={handleStatusChange}>
            <ListboxButton                     
              className={styles.RightTag}>
                {selectedState.name}
            </ListboxButton>
            <ListboxOptions anchor="bottom" 
                            className={styles.ListItem}>
              {states.map((state) => (
                <ListboxOption key={state.id} value={state} className={styles.ListItem}>
                  {state.name}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
          <Listbox value={selectedPrimaryLabel} onChange={handlePrimaryLabelChange}>
            <ListboxButton  
              className={styles.RightTag}>
                {selectedPrimaryLabel.name}
            </ListboxButton>
            <ListboxOptions anchor="bottom">
              {primaryLabels.map((primaryLabel) => (
                <ListboxOption key={primaryLabel.id} value={primaryLabel} className={styles.ListItem}>
                  {primaryLabel.name}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
          <Menu
            id={menuId}
            anchorEl={anchorEl}
            open={open}
            onClose={(e) => handleClose(e)}
            autoFocus={false}
            disableAutoFocusItem
            disableRestoreFocus
            disableEnforceFocus
            slotProps={{
              list: {
                'aria-labelledby': buttonId,
              },
              root: {
                // Forces the backdrop click-away mechanism to wait for the end of the touch
                touchEvent: 'onTouchEnd',
                disableAriaHidden: true,
              },
            }}
            // anchorOrigin={{
            //   vertical: 'top',
            //   horizontal: 'left',
            // }}
            // transformOrigin={{
            //   vertical: 'top',
            //   horizontal: 'left',
            // }}
          >
            {/* {!clone && onDuplicate && <MenuItem onClick={handleDuplicateMenuItemClicked}> <Duplicate className={styles.RightButton} onClick={onDuplicate}/></MenuItem>} */}
            {/* {!clone && onRemove && <MenuItem onClick={handleRemoveMenuItemClicked}> <Remove className={styles.RightButton} onClick={onRemove}/></MenuItem>} */}
            {onRemove && <MenuItem onClick={(e) =>handleRescheduleTomorrowSameTimeMenuItemClicked(e)}> <ListItemIcon><EditCalendar fontSize='24px'/></ListItemIcon>(Re)schedule for tomorrow (ASAP)</MenuItem>}
            {/* {onRemove && <MenuItem onClick={handleRescheduleTomorrowSameTimeMenuItemClicked}> <ListItemIcon><EditCalendar fontSize='24px'/></ListItemIcon>(Re)schedule for tomorrow (same time)</MenuItem>} */}
            {/* {onRemove && <MenuItem onClick={handleRescheduleNextWeekSameTimeMenuItemClicked}> <ListItemIcon><EditCalendar fontSize='24px'/></ListItemIcon>(Re)schedule for next week (ASAP)</MenuItem>} */}
            {onRemove && <MenuItem onClick={(e) =>handleRescheduleNextWeekSameTimeMenuItemClicked(e)}> <ListItemIcon><EditCalendar fontSize='24px'/></ListItemIcon>(Re)schedule for next week (same day/time)</MenuItem>}
            <Divider />
            {onDuplicate && <MenuItem onClick={(e) => handleDuplicateMenuItemClicked(e)}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onPointerDown={(e) => e.stopPropagation()}
                                      onTouchStart={(e) => e.stopPropagation()}
                                      onTouchEnd={(e) => e.stopPropagation()}
                                      component="button" // 💡 Forces the browser to treat it strictly as a local code hook
                                      type="button"      // 💡 Blocks it from acting as a form submit button> 
                                      >
                              <ListItemIcon><ContentCopy fontSize='20px'/></ListItemIcon>
                              Duplicate
                            </MenuItem>}
            {onRemove && <MenuItem onClick={(e) => handleRemoveMenuItemClicked(e)}> <ListItemIcon><Delete fontSize='20px'/></ListItemIcon>Delete</MenuItem>}
      
            {/* <MenuItem onClick={handleClose}>Profile</MenuItem> */}
            {/* <MenuItem onClick={handleClose}>My account</MenuItem> */}
            {/* <MenuItem onClick={handleClose}>Logout</MenuItem> */}
          </Menu>
        </div>)}
      </div>
      </li>
    );
  }
)
);

const collapseIcon = (
  <svg width="10" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 70 41">
    <path d="M30.76 39.2402C31.885 40.3638 33.41 40.995 35 40.995C36.59 40.995 38.115 40.3638 39.24 39.2402L68.24 10.2402C69.2998 9.10284 69.8768 7.59846 69.8494 6.04406C69.822 4.48965 69.1923 3.00657 68.093 1.90726C66.9937 0.807959 65.5106 0.178263 63.9562 0.150837C62.4018 0.123411 60.8974 0.700397 59.76 1.76024L35 26.5102L10.24 1.76024C9.10259 0.700397 7.59822 0.123411 6.04381 0.150837C4.4894 0.178263 3.00632 0.807959 1.90702 1.90726C0.807714 3.00657 0.178019 4.48965 0.150593 6.04406C0.123167 7.59846 0.700153 9.10284 1.75999 10.2402L30.76 39.2402Z" />
  </svg>
);
